import { type Component, createEffect, createMemo, createSignal, For, Show } from 'solid-js'
import { createStore, reconcile } from 'solid-js/store'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import type { ListEditorPropsType } from '~/types/ListEditor'

type DraftType = Record<string, unknown>

/**
 * Editor visual genérico para listas de objetos (catálogos de empresa).
 *
 * Muestra una tabla con los items y un modal para editar/agregar cada uno.
 */
const ListEditor: Component<ListEditorPropsType> = props => {
  const [draft, setDraft] = createStore<{ items: DraftType[] }>({ items: [] })
  const [modalOpen, setModalOpen] = createSignal(false)
  const [editingIndex, setEditingIndex] = createSignal<number | null>(null)
  const [formValues, setFormValues] = createSignal<DraftType>({})

  let lastEmitted = ''
  createEffect(() => {
    const serialized = JSON.stringify(props.value)
    if (serialized === lastEmitted) return
    lastEmitted = serialized
    setDraft('items', reconcile(props.value.map(item => ({ ...item }))))
  })

  const emit = (items: DraftType[]) => {
    lastEmitted = JSON.stringify(items)
    props.onChange(items.map(item => ({ ...item })))
  }

  const openAdd = () => {
    const newItem: DraftType = {}
    for (const col of props.columns) {
      newItem[col.name] = col.type === 'number' ? 0 : ''
    }
    setEditingIndex(null)
    setFormValues(newItem)
    setModalOpen(true)
  }

  const openEdit = (index: number) => {
    setEditingIndex(index)
    setFormValues({ ...draft.items[index] })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingIndex(null)
    setFormValues({})
  }

  const saveItem = () => {
    const values = formValues()
    if (editingIndex() !== null) {
      setDraft('items', editingIndex()!, values)
    } else {
      setDraft('items', draft.items.length, values)
    }
    emit(draft.items)
    closeModal()
  }

  const removeItem = (index: number) => {
    setDraft('items', prev => {
      const next = [...prev]
      next.splice(index, 1)
      return next
    })
    emit(draft.items)
  }

  const setField = (fieldName: string, rawValue: string) => {
    const col = props.columns.find(c => c.name === fieldName)
    const value = col?.type === 'number' ? Number(rawValue) || 0 : rawValue
    setFormValues(prev => ({ ...prev, [fieldName]: value }))
  }

  const errors = createMemo(() => {
    const problems: string[] = []
    if (props.uniqueKey) {
      const keys = draft.items.map(item => String(item[props.uniqueKey!] ?? ''))
      for (const item of draft.items) {
        const key = String(item[props.uniqueKey!] ?? '')
        if (key && keys.filter(k => k === key).length > 1) {
          problems.push(`Clave repetida: "${key}".`)
          break
        }
      }
    }
    return problems
  })

  return (
    <>
      <div class="space-y-3">
        <Show when={errors().length > 0}>
          <ul class="space-y-1">
            <For each={errors()}>
              {problem => (
                <li class="flex items-start gap-1.5 text-xs text-danger">
                  <Icon icon="material-symbols-light:error-outline" size={14} class="mt-0.5 shrink-0" />
                  <span>{problem}</span>
                </li>
              )}
            </For>
          </ul>
        </Show>

        <Show when={draft.items.length > 0}>
          <div class="overflow-x-auto rounded-lg border border-border">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-border bg-surface-muted/50">
                  <th class="px-3 py-2 text-left text-xs font-medium text-foreground-subtle">#</th>
                  <For each={props.columns}>{col => <th class="px-3 py-2 text-left text-xs font-medium text-foreground-subtle">{col.label}</th>}</For>
                  <th class="px-3 py-2 text-right text-xs font-medium text-foreground-subtle">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <For each={draft.items}>
                  {(item, index) => (
                    <tr class="border-b border-border last:border-b-0 hover:bg-surface-muted/30 cursor-pointer" onClick={() => openEdit(index())}>
                      <td class="px-3 py-2 text-xs text-foreground-subtle tabular-nums">{index() + 1}</td>
                      <For each={props.columns}>
                        {col => (
                          <td class="px-3 py-2 text-foreground">
                            {col.type === 'number' ? <span class="font-mono text-xs">{String(item[col.name] ?? '')}</span> : <span>{String(item[col.name] ?? '')}</span>}
                          </td>
                        )}
                      </For>
                      <td class="px-3 py-2 text-right">
                        <div class="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button size="xs" variant="text" color="default" icon="material-symbols-light:edit-outline" aria-label="Editar" onClick={() => openEdit(index())} />
                          <Button size="xs" variant="text" color="danger" icon="material-symbols-light:delete-outline" aria-label="Eliminar" onClick={() => removeItem(index())} />
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        <Show when={draft.items.length === 0}>
          <p class="text-sm text-foreground-subtle">Sin elementos.</p>
        </Show>

        <Button size="sm" variant="outlined" color="primary" icon="material-symbols-light:add" onClick={openAdd}>
          {props.addLabel ?? 'Añadir elemento'}
        </Button>
      </div>

      {/* Modal de edición */}
      <Modal isOpen={modalOpen()} onClose={closeModal} size="md" closable>
        <Modal.Header>
          <h3 class="text-lg font-semibold text-foreground">{editingIndex() !== null ? 'Editar elemento' : 'Nuevo elemento'}</h3>
        </Modal.Header>
        <Modal.Body>
          <div class="space-y-4">
            <For each={props.columns}>
              {col => (
                <Input
                  label={col.label}
                  placeholder={col.placeholder}
                  type={col.type === 'number' ? 'number' : undefined}
                  value={String(formValues()[col.name] ?? '')}
                  onInput={e => setField(col.name, e.currentTarget.value)}
                  class={col.type === 'number' ? 'font-mono text-xs' : ''}
                />
              )}
            </For>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={closeModal}>
              Cancelar
            </Button>
            <Button color="primary" onClick={saveItem}>
              {editingIndex() !== null ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ListEditor
